import InboxMetaAPI from '../../api/inboxMeta';

const state = {
  records: {}, // { inboxId: { integration_type, integration_id, status, phone_number, agentis_id } }
  isLoaded: false,
  uiFlags: {
    isLoading: false,
  },
};

// Helper function to format integration type data
const formatIntegrationTypeData = (integrationType) => {
  if (!integrationType) {
    return {
      icon: 'i-lucide-inbox',
      color: 'text-n-slate-9 dark:text-n-slate-10',
      badgeColor: 'bg-n-slate-2 text-n-slate-9',
      displayName: 'Desconhecido'
    };
  }

  const type = integrationType.toLowerCase();

  // WhatsApp (evolution/official)
  if (type === 'evolution' || type === 'official') {
    return {
      icon: 'i-ri-whatsapp-fill',
      color: 'text-grass-600 dark:text-grass-500',
      badgeColor: 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      displayName: type === 'official' ? 'WhatsApp Oficial' : 'WhatsApp Não Oficial'
    };
  }

  // Messenger
  if (type === 'messenger') {
    return {
      icon: 'i-ri-messenger-fill',
      color: 'text-n-brand',
      badgeColor: 'bg-n-iris-2 text-n-iris-11 dark:bg-n-iris-3 dark:text-n-iris-11',
      displayName: 'Messenger'
    };
  }

  // Other types
  return {
    icon: 'i-lucide-inbox',
    color: 'text-n-slate-9 dark:text-n-slate-10',
    badgeColor: 'bg-n-slate-2 text-n-slate-9',
    displayName: integrationType
  };
};

export const getters = {
  getInboxMetadata: ($state) => (inboxId) => {
    return $state.records[inboxId] || {};
  },
  
  getInboxConnectionStatus: ($state) => (inboxId) => {
    const metadata = $state.records[inboxId];
    return metadata?.status || 0;
  },
  
  getInboxIntegrationType: ($state) => (inboxId) => {
    const metadata = $state.records[inboxId];
    return metadata?.integration_type || null;
  },
  
  getInboxPhoneNumber: ($state) => (inboxId) => {
    const metadata = $state.records[inboxId];
    return metadata?.profile || null;
  },
  
  getInboxDisplayName: ($state) => (inboxId) => {
    const metadata = $state.records[inboxId];
    return metadata?.display_name || null;
  },
  
  getInboxAgentId: ($state) => (inboxId) => {
    const metadata = $state.records[inboxId];
    return metadata?.agentis_id || null;
  },
  
  getInboxIntegrationId: ($state) => (inboxId) => {
    const metadata = $state.records[inboxId];
    return metadata?.integration_id || null;
  },
  
  isMetadataLoaded: ($state) => {
    return $state.isLoaded;
  },
  
  isLoading: ($state) => {
    return $state.uiFlags.isLoading;
  },
  
  getAllInboxesMetadata: ($state) => {
    return $state.records;
  },

  // Format integration type - returns icon, color, and display name
  formatIntegrationType: () => (integrationType) => {
    return formatIntegrationTypeData(integrationType);
  },

  // Get formatted inbox data by inbox ID
  getInboxFormatted: ($state) => (inboxId) => {
    const metadata = $state.records[inboxId];
    const integrationType = metadata?.integration_type;
    return formatIntegrationTypeData(integrationType);
  },

  // Getter that formats inbox data like the load_inboxes endpoint
  getFormattedInboxes: ($state) => {
    const results = [];
    
    Object.entries($state.records).forEach(([inboxId, metadata]) => {
      const formattedInbox = {
        integration_type: metadata.integration_type,
        integration_id: metadata.integration_id,
        inbox_id: parseInt(inboxId),
        inbox_name: metadata.inbox_name || `Inbox ${inboxId}`,
        display_phone_number: metadata.phone_number || '',
      };
      
      // For evolution type, add instances array with connection status
      if (metadata.integration_type === 'evolution') {
        formattedInbox.instances = [{
          connectionStatus: metadata.status,
          ownerJid: metadata.phone_number ? `${metadata.phone_number}@s.whatsapp.net` : null,
          instanceName: metadata.integration_id,
          Chatwoot: {
            nameInbox: metadata.inbox_name || `Inbox ${inboxId}`
          }
        }];
      } else if (metadata.integration_type === 'official') {
        // For official type, add minimal instances array
        formattedInbox.instances = [{
          connectionStatus: metadata.status
        }];
      } else if (metadata.integration_type === 'messenger') {
        // For messenger type, add minimal instances array with connection status
        formattedInbox.instances = [{
          connectionStatus: metadata.status
        }];
      }

      results.push(formattedInbox);
    });
    
    return { results };
  },
};

export const actions = {
  loadAllMetadata: async ({ commit, state }) => {
    // Prevent multiple simultaneous loads
    if (state.uiFlags.isLoading) {
      return;
    }
    
    commit('SET_LOADING_FLAG', true);
    
    try {
      const response = await InboxMetaAPI.load();
      const { results } = response.data;
      
      if (results && Array.isArray(results)) {
        const metadataRecords = {};
        
        results.forEach(result => {
          const { 
            inbox_id, 
            integration_type, 
            integration_id, 
            connectionStatus,
            profile,
            inbox_name,
            display_name,
            hasAI,
            error 
          } = result;
          
          // Use connectionStatus directly from the result
          let status = connectionStatus !== undefined ? connectionStatus : 0;
          
          // Handle error case
          if (error) {
            status = 0; // Set to disconnected on error
          }
          
          metadataRecords[inbox_id] = {
            integration_type,
            integration_id,
            status,
            profile: profile || null,
            inbox_name: inbox_name || null,
            display_name: display_name || null,
            hasAI: hasAI || false,
            agentis_id: null, // Will be fetched separately if needed
          };
        });
        
        commit('SET_INBOX_METADATA_RECORDS', metadataRecords);
        commit('SET_METADATA_LOADED', true);
      }
    } catch (error) {
      // Silently handle error - logged on server side
    } finally {
      commit('SET_LOADING_FLAG', false);
    }
  },
  
  // Handle WebSocket updates from other users
  handleWebSocketUpdate: ({ commit, state }, results) => {
    if (!results || !Array.isArray(results)) {
      return;
    }
    
    const metadataRecords = {};
    
        results.forEach(result => {
          const { 
            inbox_id, 
            integration_type, 
            integration_id, 
            connectionStatus,
            profile,
            inbox_name,
            display_name,
            hasAI,
            error 
          } = result;
          
          // Use connectionStatus directly from the result
          let status = connectionStatus !== undefined ? connectionStatus : 0;
      
      // Handle error case
      if (error) {
        status = 0; // Set to disconnected on error
      }
      
      metadataRecords[inbox_id] = {
        integration_type,
        integration_id,
        status,
        profile: profile || null,
        inbox_name: inbox_name || null,
        display_name: display_name || null,
        hasAI: hasAI || state.records[inbox_id]?.hasAI || false,
        agentis_id: state.records[inbox_id]?.agentis_id || null, // Preserve existing agent info
      };
    });
    
    // Just update the store with the WebSocket data
    // The backend already sends the latest metadata in the WebSocket event
    // No need for additional API calls which would cause infinite loops
    commit('SET_INBOX_METADATA_RECORDS', metadataRecords);
  },
  
  // Optional: Fetch agent info for specific inbox
  fetchInboxAgent: async ({ commit, state }, inboxId) => {
    try {
      const response = await InboxMetaAPI.fetchAgentis(inboxId);
      const { agent } = response.data;
      
      if (state.records[inboxId]) {
        const updatedMetadata = {
          ...state.records[inboxId],
          agentis_name: agent || null,
        };
        commit('UPDATE_SINGLE_INBOX_METADATA', { inboxId, metadata: updatedMetadata });
      }
    } catch (error) {
      // Silently handle error - logged on server side
    }
  },
};

export const mutations = {
  SET_INBOX_METADATA_RECORDS: ($state, records) => {
    $state.records = records;
  },
  
  UPDATE_SINGLE_INBOX_METADATA: ($state, { inboxId, metadata }) => {
    $state.records = {
      ...$state.records,
      [inboxId]: metadata,
    };
  },
  
  SET_METADATA_LOADED: ($state, value) => {
    $state.isLoaded = value;
  },
  
  SET_LOADING_FLAG: ($state, value) => {
    $state.uiFlags.isLoading = value;
  },
  
  CLEAR_METADATA: ($state) => {
    $state.records = {};
    $state.isLoaded = false;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
